
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Box,
    Grid,
    TextField,
    InputAdornment,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Chip
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { ajaxGetPartComponents } from '../../services/partComponentService';
import Page from '../../components/Page';
import EnhancedTable from '../../components/EnhancedTable';
import { priceFormat } from '../../helpers';
import ViewDownloadIconButton from '../../components/ViewDownloadIconButton';

export default function PartComponentList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');

    const columns = [
        { id: 'manuf_part_number', label: 'Manufacturer Part Number', sortable: true },
        { id: 'manufacturer', label: 'Manufacturer', sortable: true },
        { id: 'description', label: 'Description', sortable: true },
        { 
            id: 'price', 
            label: 'Price', 
            sortable: true,
            format: (value) => priceFormat(value, 2, 6)
        },
        {
            id: 'local_price',
            label: 'Local Price',
            sortable: true,
            format: (value, row) => priceFormat(value, 2, 6, row.local_currency)
        },
        {
        id: 'active',
        label: 'Status',
        sortable: true,
        width: '100px',
        format: (value) =>
            <div style={{ textAlign: 'center' }}>
                <Chip
                    sx={{color: '#ffffff', height: '18px', fontSize: '12px'}}
                    label={value === 1 ? 'Active' : 'Inactive'}
                    color={!!value ? 'success' : 'error'}
                    size="small"
                />
            </div>
        },
        {
            id: 'part_image',
            label: 'Image',
            width: '100px',
            format: (v, row) => v &&
                <ViewDownloadIconButton
                    id={row.id}
                    fileType="part/part_image"
                    fileName={row.part_image_name}
                    fileExt="png"
                />
        }
    ];

    useEffect(() => {
        ajaxGetPartComponents()
            .then((res) => {
                const { data } = res;
                setItems(data);
            })
            .catch(() => {
                setItems([]);
            });
    }, []);

    const searchChangeHandle = (event) => {
        const new_value = event.target.value.toLowerCase();
        setSearchTerm(new_value);
    };

    const statusChangeHandle = (event) => {
        setStatusFilter(event.target.value);
    };

    useEffect(() => {
        let results = items.filter((item) => 
            `${(item.description || '')}${(item.manufacturer || '')}${(item.manuf_part_number || '')}`.toLowerCase().includes(searchTerm)
        );

        if (statusFilter !== 'all') {
            results = results.filter((item) => item.active === (statusFilter === 'active' ? 1 : 0));
        }

        setSearchResults(results);
    }, [searchTerm, items, statusFilter]);

    return (
        <Page title="Part Components" maxWidth={false} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box>
                <Grid container spacing={2}>
                    <Grid xs={12} md={12} item>
                        <TextField
                            fullWidth
                            size="small"
                            type="text"
                            label="Search"
                            placeholder="Search keyword"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            onChange={searchChangeHandle}
                            startAdornment={
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            }
                        />
                    </Grid>
                    <Grid xs={12} md={12} item>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Status</FormLabel>
                            <RadioGroup
                                row
                                value={statusFilter}
                                onChange={statusChangeHandle}
                                size="small"
                            >
                                <FormControlLabel 
                                    value="all" 
                                    control={<Radio size="small" />} 
                                    label="All" 
                                    sx={{ marginRight: 2 }}
                                />
                                <FormControlLabel 
                                    value="active" 
                                    control={<Radio size="small" />} 
                                    label="Active" 
                                    sx={{ marginRight: 2 }}
                                />
                                <FormControlLabel 
                                    value="inactive" 
                                    control={<Radio size="small" />} 
                                    label="Inactive" 
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
            <Box>
                <Grid xs={12} item>
                    <Button
                        size="small"
                        variant="contained"
                        color="info"
                        sx={{ margin: '10px 0' }}
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/partComponents/add')}
                    >
                        New Component
                    </Button>
                </Grid>
            </Box>
            <Box sx={{ flexGrow: 1, position: 'relative' }}>
                <Box sx={{ position: 'absolute', inset: '0px 0px 10px 0px' }}>
                    <EnhancedTable 
                        border 
                        fullHeight 
                        stickyColumn 
                        fontSize="12px" 
                        columns={columns} 
                        items={searchResults} 
                        onRowClick={(id) => navigate(`/partComponents/edit/${id}`)}
                    />
                </Box>
            </Box>
        </Page>
    );
}
